import Col from 'antd/es/col'
import Form from 'antd/es/form'
import InputNumber from 'antd/es/input-number'
import Row from 'antd/es/row'
import Select from 'antd/es/select'
import TreeSelect from 'antd/es/tree-select'
import { useAppSelector } from '@/redux/hooks.ts'
import { selectThemeStyle } from '@/features/setting/setting.slice.ts'
import { convertPxToNumber } from '@/utils/helpers.ts'
import IdeyaTypography from '@/common/antd/ideya-typography.tsx'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useMemo,
  useState,
} from 'react'
import { IdeyaButton } from '@/common/antd/ideya-button/ideya-button.tsx'
import { Checkbox, FormInstance, FormProps } from 'antd/lib/index'
import Radio from 'antd/es/radio/index'
import {
  useGetTickerShortableQuery,
  useLazyGetTickerPriceQuery,
  useLazySearchTickerQuery,
} from '@/apis/ticker/ticker-api.api.ts'
import { debounce } from 'lodash'
import {
  FillStrategies,
  HorizonOptions,
  ConvictionOptions,
  TickerOption,
  getTradeReasonsTreeData,
  IDEA_MUTATION_KEYS,
} from './action-idea-form.ts'
import { UserInfo } from '@/apis/auth/auth.def.ts'
import {
  useCheckFillStrategyQuery,
  useCheckIdeaExistQuery,
  useCreateIdeaMutation,
  useUpdateIdeaMutation,
} from '@/apis/idea/idea-api.api.ts'
import { getErrorDescription } from '@/redux/utils.ts'
import {
  IdeaCreateRequest,
  IdeaDetail,
  IdeaUpdateRequest,
} from '@/apis/idea/idea-api.def.ts'
import { RuleObject } from 'antd/es/form/index'
import { useFixedBreakpoint } from '@/common/hooks/use-is-mobile.ts'
import { useTranslation } from 'react-i18next'
import { InvalidTickerMessage } from './invalid-ticker-message.tsx'
import App from 'antd/es/app/index'
import { skipToken } from '@reduxjs/toolkit/query'
// import { NEW_RELIC, newRelic } from '@/utils/new-relic.ts'
import Switch from 'antd/es/switch/index'
import { IdeaDirectionType } from '@/enums/idea-list.enum.ts'
import theme from 'antd/es/theme/index'
import './action-idea-form.scss'
import { currencyValueFormatter } from '@/utils/formatters.ts'
import { ValueFormatterParams } from 'ag-grid-community'
import { NEW_RELIC, newRelic } from '@/utils/new-relic.ts'
import { RebalanceNote } from './rebalance-note.tsx'

// Transition MS Antd Segmented Thumb (Animation on changed selection)
const THUMB_TRANSITION_MS = 200
const MAX_TRADE_CAP = 5_000_000

const { useToken } = theme

function normalizeTradeReasonValue(value?: string | string[]): string {
  const reasonList = Array.isArray(value)
    ? value
    : (value ?? '').split(',').map((reason) => reason.trim())

  return reasonList
    .map((reason) => reason.trim())
    .filter(Boolean)
    .sort()
    .join(',')
}

export type CreateIdeaFormFieldTypes = {
  figi: string
  direction: IdeaDirectionType | ''
  targetMarketValue: number
  target: number
  stop: number
  fillStrategy: string
  tradeReason: string | string[]
  horizon: string
  conviction: string
  comment: string
  auto_fill: boolean
}

interface CreateIdeaFormProps {
  onCancel: () => void
  onConfirm: (ticker: {
    tickerLabel: string
    tickerCurrency?: string
    hasTargetMarketValueEdited: boolean
  }) => void
  form: FormInstance<CreateIdeaFormFieldTypes>
  buyingPower?: number
  usedBuyingPower?: number
  userInfo: UserInfo
  onIdeaActioned?: () => void
  mode?: 'create' | 'edit'
  ideaDetail?: IdeaDetail
  onEditIdea?: (ideaId: string) => void
  onIdeaLiquidated: () => void
}

export interface CreateIdeaFormRef {
  resetFormState: () => void
}

export const ActionIdeaForm = forwardRef<
  CreateIdeaFormRef,
  CreateIdeaFormProps
>(
  (
    {
      onCancel,
      onConfirm,
      form,
      buyingPower,
      usedBuyingPower,
      userInfo,
      onIdeaActioned,
      ideaDetail,
      mode = 'create',
      onEditIdea,
      onIdeaLiquidated,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const { message: messageApi, notification: notificationApi } = App.useApp()
    const tradeReasonsTreeData = useMemo(() => getTradeReasonsTreeData(t), [t])
    const themeStyle = useAppSelector(selectThemeStyle)
    const { token } = useToken()
    const targetSectionBgColor = {
      Long: themeStyle.green1,
      Short: themeStyle.red1,
      '': token.colorBgLayout,
    }

    const [visualDirection, setVisualDirection] = useState<
      IdeaDirectionType | ''
    >('')
    const [isMaxFillReached, setIsMaxFillReached] = useState<boolean>(false)
    const [horizonValue, setHorizonValue] = useState<string>(
      ideaDetail?.horizon ?? ''
    )
    const [convictionValue, setConvictionValue] = useState<string>(
      ideaDetail?.conviction ?? ''
    )
    const [tickerOptions, setTickerOptions] = useState<TickerOption[]>([])
    const [companyName, setCompanyName] = useState<string>('')
    const [maxTargetMarketValue, setMaxTargetMarketValue] =
      useState<number>(MAX_TRADE_CAP)
    const [selectedBbgCompositeKey, setSelectedBbgCompositeKey] =
      useState<string>(ideaDetail?.bbgCompositeKey ?? '')
    const [selectedFigi, setSelectedFigi] = useState<string>('')
    // For mobile only
    const [selectedTradeReasons, setSelectedTradeReasons] = useState<string[]>(
      []
    )
    const [showCannotShortMessage, setShowCannotShortMessage] =
      useState<boolean>(false)
    const [directionError, setDirectionError] = useState<string>('')
    const [hasTargetMarketValueEdited, setHasTargetMarketValueEdited] =
      useState(false)
    const isGlobal = userInfo.region === 'Global'

    const MinTargetMarketValue = isGlobal ? 0 : 600000
    const TargetMarketValueStep = 100000
    const availableCapital = (
      (buyingPower ?? 0) - (usedBuyingPower ?? 0)
    ).toLocaleString(undefined, { maximumFractionDigits: 0 })
    const screens = useFixedBreakpoint()
    const isMobile = !screens.md
    const initialTargetMarketValue = Math.round(
      ideaDetail?.targetMarketValue ?? 0
    )

    // Special check for JP and Global users (Different BP value buttons)

    const isJpOrGlobal = userInfo.region === 'Japan' || isGlobal

    const { data: fillStrategyData, isLoading: fillStrategyLoading } =
      useCheckFillStrategyQuery(
        ideaDetail
          ? {
              id: ideaDetail.id,
              fillStrategy: ideaDetail.fillStrategy,
            }
          : skipToken,
        {
          refetchOnMountOrArgChange: true,
          skip: !ideaDetail,
        }
      )

    const [fetchTickerSearch, { data: tickerData, isLoading: tickerLoading }] =
      useLazySearchTickerQuery()

    const [fetchTickerPrice, { data: tickerPriceData }] =
      useLazyGetTickerPriceQuery()

    const [createIdea, { isSuccess, isError, error, reset }] =
      useCreateIdeaMutation({ fixedCacheKey: IDEA_MUTATION_KEYS.CREATE })
    // fixedCacheKey shares the mutation result with every mounted form (the
    // layout and idea-list each render one), so only the form that submitted
    // may react to success; otherwise each mounted form shows its own toast.
    const hasSubmittedRef = useRef(false)

    const [
      updateIdea,
      {
        isSuccess: isUpdateSuccess,
        isError: isUpdateError,
        error: updateError,
        reset: updateReset,
      },
    ] = useUpdateIdeaMutation({ fixedCacheKey: IDEA_MUTATION_KEYS.UPDATE })

    const { data: isShortableData, isLoading: isShortableLoading } =
      useGetTickerShortableQuery(
        { bbgCompositeKey: selectedBbgCompositeKey },
        {
          skip: !selectedBbgCompositeKey,
          refetchOnMountOrArgChange: true,
          refetchOnReconnect: true,
        }
      )

    const {
      currentData: checkIdeaExistData,
      isLoading: checkIdeaExistLoading,
    } = useCheckIdeaExistQuery({ figi: selectedFigi }, { skip: !selectedFigi })

    useEffect(() => {
      if (ideaDetail) {
        const tickerOption: TickerOption = {
          label: ideaDetail.bbgCompositeKey,
          bbgCompositeKey: ideaDetail.bbgCompositeKey,
          name: ideaDetail.ticker.name,
          legalName: ideaDetail.ticker.legalName,
          value: ideaDetail.ticker.figi,
          status: ideaDetail.ticker.status,
        }

        fetchTickerSearch({ keyword: ideaDetail.ticker.figi })
        fetchTickerPrice({ bbgCompositeKey: ideaDetail.ticker.bbgCompositeKey })
        setSelectedBbgCompositeKey(ideaDetail.ticker.bbgCompositeKey)

        setTickerOptions([tickerOption])
        let finalCompanyName = ideaDetail.ticker.name
        if (ideaDetail.ticker.legalName) {
          finalCompanyName = finalCompanyName.concat(
            ' (' + ideaDetail.ticker.legalName + ')'
          )
        }
        setCompanyName(finalCompanyName)
        setHorizonValue(ideaDetail.horizon)
        setConvictionValue(ideaDetail.conviction)
        setVisualDirection(ideaDetail.direction as IdeaDirectionType)

        const tradeReasonsArray =
          ideaDetail.tradeReason?.split(',').map((reason) => reason.trim()) ??
          null
        setSelectedTradeReasons(tradeReasonsArray)
        form.setFieldsValue({
          tradeReason: tradeReasonsArray,
          direction: ideaDetail.direction as IdeaDirectionType,
        })
      }
    }, [fetchTickerPrice, fetchTickerSearch, ideaDetail, isShortableData, form])

    // Edit-mode target market value cap: never below the existing position so it
    // can always be reduced; small ideas keep the 5M allowance.
    useEffect(() => {
      if (!ideaDetail || isGlobal) return

      // Short on a non-shortable ticker: can't add more, cap at existing size.
      if (
        isShortableData &&
        fillStrategyData &&
        !isShortableData.data.isShortable &&
        ideaDetail.direction === IdeaDirectionType.Short
      ) {
        setMaxTargetMarketValue(
          fillStrategyData.data.hasUnfilledAction
            ? ideaDetail.targetMarketValue
            : ideaDetail.currentMarketValue
        )
        return
      }

      const maxUpSize = ideaDetail.ticker?.maxUpSize
        ? parseFloat(ideaDetail.ticker.maxUpSize)
        : 0
      setMaxTargetMarketValue(
        Math.round(
          Math.max(
            Math.min(ideaDetail.currentMarketValue + maxUpSize, MAX_TRADE_CAP),
            ideaDetail.currentMarketValue
          )
        )
      )
    }, [ideaDetail, isGlobal, isShortableData, fillStrategyData])

    useEffect(() => {
      if (mode === 'create' && isGlobal && buyingPower) {
        setMaxTargetMarketValue(buyingPower)
      }
    }, [mode, userInfo.region, buyingPower, isGlobal])

    // Update ticker options when API data changes
    useEffect(() => {
      if (tickerData?.data?.tickers) {
        setTickerOptions(
          tickerData.data.tickers.map((t) => ({
            key: t.figi,
            value: t.figi,
            label: `${t.tickerCode} - ${t.name}`,
            bbgCompositeKey: t.bbgCompositeKey,
            name: t.name,
            legalName: t.legalName,
            status: t.status,
          }))
        )
      }
    }, [tickerData])

    // Move success, updateSuccess, errorNotification inside useEffect or wrap with useCallback
    const success = useCallback(() => {
      messageApi.open({
        type: 'success',
        content: t('idea_list.idea_modal.action_idea_form.success_message'),
      })
    }, [t, messageApi])

    const updateSuccess = useCallback(() => {
      messageApi.open({
        type: 'success',
        content: t('idea_list.idea_modal.action_idea_form.update_message'),
      })
    }, [t, messageApi])

    const errorNotification = useCallback(
      (description: string) => {
        notificationApi.error({
          message: 'Error',
          description,
          placement: 'topRight',
        })
      },
      [notificationApi]
    )

    const onCloseForm = useCallback(() => {
      // Only trigger the close here; field and local-state clearing is deferred
      // to the modal's afterClose so the data stays visible during the close
      // animation instead of flashing empty before the modal disappears.
      onCancel()
    }, [onCancel])

    const onConfirmForm = useCallback(() => {
      form.validateFields().then(() => {
        const selectedTicker = tickerData?.data?.tickers.find(
          (t) => t.figi === form.getFieldValue('figi')
        )
        onConfirm({
          tickerLabel:
            mode === 'create'
              ? `${selectedTicker?.tickerCode ?? ''} - ${selectedTicker?.name ?? ''}`
              : (ideaDetail?.bbgCompositeKey ?? ''),
          tickerCurrency:
            mode === 'create'
              ? selectedTicker?.currency
              : ideaDetail?.tickerCurrency,
          hasTargetMarketValueEdited,
        })
      })
    }, [
      form,
      onConfirm,
      tickerData,
      ideaDetail?.bbgCompositeKey,
      mode,
      ideaDetail?.tickerCurrency,
      hasTargetMarketValueEdited,
    ])

    useEffect(() => {
      if (!hasSubmittedRef.current) return

      if (isSuccess) {
        hasSubmittedRef.current = false
        // Close first so the modal-close render commits immediately,
        // then reset the mutation state and trigger deferred refetch.
        onCloseForm()
        success()
        reset()
        if (onIdeaActioned) onIdeaActioned() // call after success
      }

      if (isUpdateSuccess) {
        hasSubmittedRef.current = false
        onCloseForm()
        updateSuccess()
        updateReset()
        if (onIdeaActioned) onIdeaActioned()
      }
    }, [
      isSuccess,
      form,
      onCancel,
      reset,
      onIdeaActioned,
      isUpdateSuccess,
      updateReset,
      success,
      onCloseForm,
      updateSuccess,
    ])

    useEffect(() => {
      if (isError) {
        hasSubmittedRef.current = false
        reset()
      }

      if (isUpdateError) {
        hasSubmittedRef.current = false
        updateReset()
      }
    }, [isError, error, reset, isUpdateError, updateError, updateReset])

    useEffect(() => {
      if (
        checkIdeaExistData &&
        checkIdeaExistData.data.exist &&
        checkIdeaExistData.data.ideaId
      ) {
        if (onEditIdea) {
          onEditIdea(checkIdeaExistData.data.ideaId)
        }
      }
    }, [checkIdeaExistData, checkIdeaExistLoading, onEditIdea])

    // Debounced search handler
    const handleTickerSearch = debounce((value: string) => {
      if (value && value.length >= 2) {
        fetchTickerSearch({ keyword: value })
      } else {
        setTickerOptions([])
      }
    }, 400)

    const handleDirectionChange = (val: IdeaDirectionType | '') => {
      form.setFieldsValue({
        direction: val,
      })
      setTimeout(() => setVisualDirection(val), THUMB_TRANSITION_MS)
    }

    // Single source of truth for the target market value ceiling.
    // Direct input, the %BP buttons and the "Maxfill will be" hint must all
    // derive from `maxTargetMarketValue` so their limits never diverge.
    const clampToMaxTargetMarketValue = (value: number) =>
      isGlobal ? value : Math.min(value, maxTargetMarketValue)

    const isOverMaxTargetMarketValue = (value: number) =>
      !isGlobal && value > maxTargetMarketValue

    const handleTargetMarketValueBp = (percentage: number) => () => {
      if (!buyingPower) return

      const calculatedValue = Math.round(buyingPower * percentage)
      if (!calculatedValue) return

      if (mode === 'edit') setHasTargetMarketValueEdited(true)
      setIsMaxFillReached(isOverMaxTargetMarketValue(calculatedValue))
      form.setFieldsValue({
        targetMarketValue: clampToMaxTargetMarketValue(calculatedValue),
      })

      if (
        mode !== 'create' &&
        ideaDetail &&
        form.getFieldValue('direction') === IdeaDirectionType.Short &&
        isShortableData &&
        !isShortableData.data.isShortable &&
        form.getFieldValue('targetMarketValue') > ideaDetail.targetMarketValue
      ) {
        setShowCannotShortMessage(true)
      } else {
        setShowCannotShortMessage(false)
      }
    }

    const handleTargetPrice = (percentage: number) => () => {
      const price = tickerPriceData?.data?.price
      if (!price) return

      const direction = form.getFieldValue('direction')
      const multiplier =
        direction === IdeaDirectionType.Short ? 1 - percentage : 1 + percentage
      form.setFieldsValue({
        target: parseFloat((price * multiplier).toFixed(2)),
      })
    }

    const handleStopPrice = (percentage: number) => () => {
      const price = tickerPriceData?.data?.price
      if (!price) return

      const direction = form.getFieldValue('direction')
      const multiplier =
        direction === IdeaDirectionType.Short ? 1 + percentage : 1 - percentage
      form.setFieldsValue({
        stop: parseFloat((price * multiplier).toFixed(2)),
      })
    }

    const handleHorizonClick = (value: string) => {
      setHorizonValue(value)
      form.setFieldsValue({ horizon: value })
    }

    const handleConvictionClick = (value: string) => {
      setConvictionValue(value)
      form.setFieldsValue({ conviction: value })
    }

    // Set company name when ticker is selected and fetch ticker price
    const handleTickerSelect = (value: string) => {
      form.resetFields(['targetMarketValue', 'direction', 'target', 'stop'])
      const selected = tickerOptions.find((t) => t.value === value)
      let finalCompanyName = selected?.name ?? ''
      if (finalCompanyName && selected?.legalName) {
        finalCompanyName = finalCompanyName.concat(
          ' (' + selected?.legalName + ')'
        )
      }
      setCompanyName(finalCompanyName)
      if (selected?.value) {
        setSelectedFigi(selected.value)
      }
      if (selected?.bbgCompositeKey) {
        fetchTickerPrice({ bbgCompositeKey: selected.bbgCompositeKey })
        setSelectedBbgCompositeKey(selected?.bbgCompositeKey)
      }

      if (!isGlobal && buyingPower) {
        const maxUpSize =
          tickerData?.data?.tickers?.find(
            (t) => selected?.bbgCompositeKey === t.bbgCompositeKey
          )?.maxUpSize ?? maxTargetMarketValue
        setMaxTargetMarketValue(maxUpSize as number)

        const targetMarketValue = form.getFieldValue('targetMarketValue')

        if (maxUpSize && targetMarketValue > maxUpSize) {
          setIsMaxFillReached(true)
          form.setFieldsValue({ targetMarketValue: maxUpSize as number })
        } else {
          setIsMaxFillReached(false)
        }
      }
      if (visualDirection === '') {
        checkDirectionError(visualDirection)
      } else {
        setVisualDirection('')
      }
    }

    const onFinish: FormProps<CreateIdeaFormFieldTypes>['onFinish'] = async (
      values
    ) => {
      newRelic.recordCustomEvent('FormAction', {
        element: NEW_RELIC.FORM_ACTION.submitCreateIdea,
        action: 'click',
      })
      let isCreateNeeded = false
      let payload: IdeaCreateRequest = {} as IdeaCreateRequest
      try {
        const tradeReason = Array.isArray(values.tradeReason)
          ? values.tradeReason.join(',')
          : values.tradeReason

        payload = {
          figi: values.figi,
          direction: values.direction,
          targetMarketValue: values.targetMarketValue,
          targetPrice: values.target,
          stopPrice: values.stop,
          conviction: values.conviction,
          horizon: values.horizon,
          tradeReason,
          fillStrategy: values.fillStrategy,
          description: values.comment,
          auto_fill: values.auto_fill,
        }
        isCreateNeeded = true
      } catch (err) {
        const description = getErrorDescription(err)
        errorNotification(description)
        isCreateNeeded = false
      }

      // RTK mutation errors are handled centrally by the `isError` effect,
      // so don't `.unwrap()` here (that would double-report via catch).
      // The try/catch only surfaces non-RTK errors thrown while building the
      // payload above.
      if (isCreateNeeded) {
        hasSubmittedRef.current = true
        createIdea(payload)
      }
    }

    const watchedTarget = Form.useWatch('target', form)
    const watchedStop = Form.useWatch('stop', form)
    const watchedConviction = Form.useWatch('conviction', form)
    const watchedHorizon = Form.useWatch('horizon', form)
    const watchedTradeReason = Form.useWatch('tradeReason', form) as
      string | string[] | undefined
    const watchedFillStrategy = Form.useWatch('fillStrategy', form)
    const watchedAutoFill = Form.useWatch('auto_fill', form)
    const watchedTargetMarketValue = Form.useWatch('targetMarketValue', form)

    // Edit mode: an unchanged target sends no order even when the position has
    // drifted from it. Rebalance flags the target as edited so submit re-sends
    // it; Reset restores the original target and clears the flag.
    const currentMarketValue = Math.round(ideaDetail?.currentMarketValue ?? 0)

    const handleRebalance = () => {
      setHasTargetMarketValueEdited(true)
    }

    const handleRebalanceReset = () => {
      form.setFieldsValue({ targetMarketValue: initialTargetMarketValue })
      form.validateFields(['targetMarketValue']).catch(() => undefined)
      setHasTargetMarketValueEdited(false)
      setIsMaxFillReached(false)
      setShowCannotShortMessage(false)
    }

    const { hasEditedUpdateValues, hasFillStrategyEdited } = useMemo(() => {
      if (mode !== 'edit' || !ideaDetail)
        return {
          hasEditedUpdateValues: false,
          hasFillStrategyEdited: false,
        }

      const initialFillStrategy =
        ideaDetail.fillStrategy === '15min-VWAP' ||
        ideaDetail.fillStrategy === '1hour-VWAP'
          ? FillStrategies[0].value
          : ideaDetail.fillStrategy

      const initialTradeReason = normalizeTradeReasonValue(
        ideaDetail.tradeReason
      )

      const hasOtherFieldEdited =
        (watchedTarget ?? ideaDetail.target) !== ideaDetail.target ||
        (watchedStop ?? ideaDetail.stop) !== ideaDetail.stop ||
        (watchedConviction ?? ideaDetail.conviction) !==
          ideaDetail.conviction ||
        (watchedHorizon ?? ideaDetail.horizon) !== ideaDetail.horizon ||
        normalizeTradeReasonValue(watchedTradeReason ?? initialTradeReason) !==
          initialTradeReason ||
        (watchedAutoFill ?? ideaDetail.auto_fill) !== ideaDetail.auto_fill
      const hasFillStrategyEdited =
        (watchedFillStrategy ?? initialFillStrategy) !== initialFillStrategy

      return {
        hasEditedUpdateValues:
          hasTargetMarketValueEdited ||
          hasOtherFieldEdited ||
          hasFillStrategyEdited,
        hasFillStrategyEdited,
      }
    }, [
      mode,
      ideaDetail,
      hasTargetMarketValueEdited,
      watchedTarget,
      watchedStop,
      watchedConviction,
      watchedHorizon,
      watchedTradeReason,
      watchedFillStrategy,
      watchedAutoFill,
    ])

    // A fill strategy change re-sends the target on submit, so the note would
    // contradict the payload.
    const showRebalanceNote =
      mode === 'edit' &&
      !!ideaDetail &&
      typeof watchedTargetMarketValue === 'number' &&
      !(hasFillStrategyEdited && !hasTargetMarketValueEdited)

    const onUpdate: FormProps<CreateIdeaFormFieldTypes>['onFinish'] = async (
      values
    ) => {
      let isUpdateNeeded = false
      const payload: IdeaUpdateRequest = {} as IdeaUpdateRequest
      try {
        if (!hasEditedUpdateValues) return

        const tradeReason = Array.isArray(values.tradeReason)
          ? values.tradeReason.join(',')
          : values.tradeReason

        // Match the same fillStrategy mapping used when pre-filling edit form
        const initialFillStrategy =
          ideaDetail?.fillStrategy === '15min-VWAP' ||
          ideaDetail?.fillStrategy === '1hour-VWAP'
            ? FillStrategies[0].value
            : ideaDetail?.fillStrategy

        const hasFillStrategyChanged =
          values.fillStrategy !== initialFillStrategy
        if (hasFillStrategyChanged || hasTargetMarketValueEdited) {
          payload.targetMarketValue = values.targetMarketValue
        }

        payload.id = ideaDetail?.id ?? ''
        payload.targetPrice = values.target
        payload.stopPrice = values.stop
        payload.conviction = values.conviction
        payload.horizon = values.horizon
        payload.tradeReason = tradeReason
        payload.fillStrategy = values.fillStrategy
        payload.auto_fill = values.auto_fill
        isUpdateNeeded = true
      } catch (err) {
        const description = getErrorDescription(err)
        errorNotification(description)
        isUpdateNeeded = false
      }

      // RTK mutation errors are handled centrally by the `isUpdateError`
      // effect, so don't `.unwrap()` here. The try/catch only surfaces
      // non-RTK errors thrown while building the payload above.
      if (isUpdateNeeded) {
        hasSubmittedRef.current = true
        updateIdea(payload)
      }
    }

    // Expose form controls to parent via ref
    useImperativeHandle(ref, () => ({
      resetFormState: () => {
        setHorizonValue('')
        setConvictionValue('')
        setSelectedFigi('')
        setCompanyName('')
        setHasTargetMarketValueEdited(false)
      },
    }))

    const checkDirectionError = useCallback(
      (currentDirection: IdeaDirectionType | '') => {
        if (currentDirection || !form.getFieldValue('figi')) {
          setDirectionError('')
        } else {
          form.validateFields(['direction']).catch(({ errorFields }) => {
            const err = errorFields?.find(
              (f: { name: string[] }) => f.name[0] === 'direction'
            )

            if (err) setDirectionError(err.errors[0])
          })
        }
      },
      [form]
    )

    useEffect(() => {
      if (mode !== 'create') return
      checkDirectionError(visualDirection)
    }, [visualDirection, checkDirectionError, mode])

    const canShort =
      mode !== 'edit' &&
      (!form.getFieldValue('figi') ||
        (!isShortableLoading && isShortableData?.data.isShortable))

    // Quick-select button configs (rendered via .map to avoid repetition)
    const bpButtons = (
      isJpOrGlobal
        ? [0.01, 0.02, 0.03, 0.04, 0.05]
        : [0.02, 0.04, 0.06, 0.08, 0.1]
    ).map((percentage) => ({
      percentage,
      label: `${Math.round(percentage * 100)}% BP`,
    }))

    const targetPriceButtons = [0.1, 0.2, 0.3, 0.4, 0.5].map((percentage) => ({
      percentage,
      label: `${Math.round(percentage * 100)}%`,
    }))

    const stopPriceButtons = [0.05, 0.1, 0.15, 0.2, 0.3].map((percentage) => ({
      percentage,
      label: `${Math.round(percentage * 100)}%`,
    }))

    return (
      <>
        <Form
          form={form}
          layout={'vertical'}
          onFinish={mode === 'create' ? onFinish : onUpdate}
          onFinishFailed={() => {
            checkDirectionError(visualDirection)
          }}
          initialValues={{
            targetMarketValue: 1000000,
          }}
        >
          <div className={'action-idea-form__content'}>
            <div className={'action-idea-form__body'}>
              <Row
                gutter={
                  isMobile
                    ? [0, 0]
                    : [
                        convertPxToNumber(themeStyle.marginBase),
                        convertPxToNumber(themeStyle.marginBase),
                      ]
                }
                align={isMobile ? 'stretch' : 'middle'}
                style={{
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <Col
                  span={isMobile ? 24 : 8}
                  style={isMobile ? { marginBottom: 8 } : {}}
                >
                  <Form.Item
                    label={t('idea_list.idea_modal.action_idea_form.ticker')}
                    name={'figi'}
                    style={{ width: '100%' }}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'idea_list.idea_modal.action_idea_form.ticker_required'
                        ),
                      },
                      ...(mode === 'create'
                        ? [
                            {
                              validator: (_: RuleObject, value: string) => {
                                const tickerOption = tickerOptions.find(
                                  (option) => option.value === value
                                )
                                if (
                                  value &&
                                  tickerOption?.status === 'Inactive'
                                ) {
                                  return Promise.reject(
                                    <InvalidTickerMessage
                                      message={t(
                                        'idea_list.idea_modal.action_idea_form.ticker_market_cap_adv_limit'
                                      )}
                                      ticker={tickerOption.label}
                                    />
                                  )
                                }
                                return Promise.resolve()
                              },
                            },
                          ]
                        : []),
                    ]}
                    help={(() => {
                      const currentValue = form.getFieldValue('figi')
                      const tickerOption = tickerOptions.find(
                        (option) => option.value === currentValue
                      )

                      // Show validation error first if ticker is inactive
                      if (
                        currentValue &&
                        tickerOption?.status === 'Inactive' &&
                        mode === 'create'
                      ) {
                        return (
                          <>
                            <InvalidTickerMessage
                              message={t(
                                'idea_list.idea_modal.action_idea_form.ticker_market_cap_adv_limit'
                              )}
                              ticker={tickerOption.label}
                            />
                          </>
                        )
                      }
                    })()}
                  >
                    <Select
                      allowClear={mode === 'create'}
                      showSearch
                      placeholder={t(
                        'idea_list.idea_modal.action_idea_form.add_active_ticker'
                      )}
                      filterOption={false}
                      onSearch={
                        mode === 'create' ? handleTickerSearch : undefined
                      }
                      options={tickerOptions}
                      loading={tickerLoading}
                      notFoundContent={
                        tickerLoading
                          ? t('idea_list.idea_modal.action_idea_form.loading')
                          : t(
                              'idea_list.idea_modal.action_idea_form.no_tickers_found'
                            )
                      }
                      onSelect={
                        mode === 'create' ? handleTickerSelect : undefined
                      }
                      onClear={
                        mode === 'create'
                          ? () => {
                              setTickerOptions([])
                              setCompanyName('')
                            }
                          : undefined
                      }
                      disabled={mode == 'edit'}
                      styles={{
                        popup: {
                          root: isMobile
                            ? { minWidth: 0, width: '90vw', maxWidth: '90vw' }
                            : {},
                        },
                      }}
                      style={isMobile ? { width: '100%' } : {}}
                    />
                  </Form.Item>
                </Col>
                {companyName && (
                  <Col style={{ marginTop: themeStyle.paddingBase }}>
                    <IdeyaTypography.Text type={'secondary'}>
                      {companyName}
                    </IdeyaTypography.Text>
                  </Col>
                )}
              </Row>
              <Row
                style={{
                  height: '100%',
                  borderTop: isMobile ? '' : '1px solid rgba(0, 0, 0, 0.15)',
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <Col
                  sm={12}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: isMobile ? '0px 0px 12px' : '12px 20px 12px 0px',
                    borderRight: isMobile
                      ? undefined
                      : '1px solid rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <Row
                    align={'middle'}
                    justify={'space-between'}
                    style={{ marginBottom: directionError ? '0px' : '10px' }}
                  >
                    <Form.Item
                      name={'direction'}
                      style={{ marginBottom: '0px' }}
                      rules={[
                        {
                          required: true,
                          message: `⚠️ ${t(
                            'idea_list.idea_modal.action_idea_form.select_trade_side_first'
                          )}`,
                        },
                      ]}
                      help={''}
                      validateStatus={''}
                    >
                      <Row gutter={[convertPxToNumber(themeStyle.marginXS), 0]}>
                        <Col>
                          <IdeyaButton
                            onClick={() =>
                              handleDirectionChange(IdeaDirectionType.Long)
                            }
                            disabled={mode === 'edit'}
                            style={
                              mode === 'edit'
                                ? {}
                                : {
                                    borderColor: themeStyle.colorLongBorder,
                                    backgroundColor:
                                      visualDirection === IdeaDirectionType.Long
                                        ? themeStyle.colorLong
                                        : themeStyle.colorLongBg,
                                    color:
                                      visualDirection === IdeaDirectionType.Long
                                        ? token.colorTextLightSolid
                                        : themeStyle.colorLong,
                                  }
                            }
                          >
                            {t('idea_list.idea_table.direction.long')}
                          </IdeyaButton>
                        </Col>
                        <Col>
                          <IdeyaButton
                            onClick={() =>
                              handleDirectionChange(IdeaDirectionType.Short)
                            }
                            disabled={!canShort}
                            style={
                              !canShort
                                ? {}
                                : {
                                    borderColor: themeStyle.colorShortBorder,
                                    backgroundColor:
                                      visualDirection ===
                                      IdeaDirectionType.Short
                                        ? themeStyle.colorShort
                                        : themeStyle.colorShortBg,
                                    color:
                                      visualDirection ===
                                      IdeaDirectionType.Short
                                        ? token.colorTextLightSolid
                                        : themeStyle.colorShort,
                                  }
                            }
                          >
                            {t('idea_list.idea_table.direction.short')}
                          </IdeyaButton>
                        </Col>
                      </Row>
                    </Form.Item>
                    <IdeyaTypography.Text>
                      {t('idea_list.idea_modal.action_idea_form.unit')} : USD
                    </IdeyaTypography.Text>
                  </Row>
                  {directionError && (
                    <Row style={{ marginBottom: '10px' }}>
                      <IdeyaTypography.Text type={'danger'}>
                        {directionError}
                      </IdeyaTypography.Text>
                    </Row>
                  )}
                  <Row
                    style={{
                      flex: 1,
                      padding: isMobile ? 8 : '0px 1.25rem',
                      flexDirection: 'column',
                      justifyContent: 'space-evenly',
                      alignItems: 'flex-start',
                      alignSelf: 'stretch',
                      backgroundColor:
                        targetSectionBgColor[visualDirection ?? ''],
                      borderRadius: '0.25rem',
                      ...(isMobile ? { gap: '1.25rem' } : undefined),
                    }}
                  >
                    <div style={{ width: '100%' }}>
                      <Form.Item
                        className={'target-market-label-item'}
                        name={'targetMarketValue'}
                        style={{ width: '100%', marginBottom: '8px' }}
                        label={
                          <div
                            className={'custom-label'}
                            style={{
                              display: isMobile ? '' : 'flex',
                              justifyContent: 'space-between',
                              width: '100%',
                              minWidth: 0,
                            }}
                          >
                            <IdeyaTypography.Text
                              style={{ marginRight: isMobile ? '8px' : '' }}
                            >
                              {t(
                                'idea_list.idea_modal.action_idea_form.target_market_value'
                              )}
                            </IdeyaTypography.Text>
                            <IdeyaTypography.Text style={{ color: '#EB855D' }}>
                              {isMobile
                                ? t(
                                    'idea_list.idea_modal.action_idea_form.available'
                                  )
                                : t(
                                    'idea_list.idea_modal.action_idea_form.available_capital'
                                  )}
                              : {availableCapital}
                            </IdeyaTypography.Text>
                          </div>
                        }
                        help={
                          isMaxFillReached && !isGlobal ? (
                            <span style={{ color: themeStyle.agGridTableRed }}>
                              Maxfill will be $
                              {maxTargetMarketValue.toLocaleString()}
                            </span>
                          ) : null
                        }
                        rules={[
                          {
                            required: true,
                            message: t(
                              'idea_list.idea_modal.action_idea_form.target_market_value_required'
                            ),
                          },
                          ...(mode === 'edit'
                            ? [
                                {
                                  validator: (
                                    _: RuleObject,
                                    value: unknown
                                  ) => {
                                    if (isGlobal || typeof value !== 'number') {
                                      return Promise.resolve()
                                    }

                                    if (value <= maxTargetMarketValue) {
                                      return Promise.resolve()
                                    }

                                    const isInitialEditValue =
                                      mode === 'edit' &&
                                      ideaDetail &&
                                      initialTargetMarketValue === value

                                    if (isInitialEditValue) {
                                      return Promise.resolve()
                                    }

                                    return Promise.reject(
                                      `${t(
                                        'idea_list.idea_modal.action_idea_form.target_market_value_cannot_exceed_max_value'
                                      )}: ${maxTargetMarketValue.toLocaleString()}`
                                    )
                                  },
                                },
                              ]
                            : []),
                        ]}
                      >
                        <InputNumber<number>
                          controls={false}
                          style={{ width: '100%' }}
                          formatter={(value) =>
                            `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                          parser={(value) =>
                            value?.replace(
                              /\$\s?|(,*)/g,
                              ''
                            ) as unknown as number
                          }
                          precision={0}
                          onInput={() => {
                            if (mode === 'edit') {
                              setHasTargetMarketValueEdited(true)
                            }
                          }}
                          onChange={(value) => {
                            if (!value) {
                              setIsMaxFillReached(false)
                              setShowCannotShortMessage(false)
                              return
                            }

                            setIsMaxFillReached(
                              isOverMaxTargetMarketValue(value)
                            )

                            if (
                              mode !== 'create' &&
                              ideaDetail &&
                              form.getFieldValue('direction') ===
                                IdeaDirectionType.Short &&
                              isShortableData &&
                              !isShortableData.data.isShortable &&
                              form.getFieldValue('targetMarketValue') >
                                ideaDetail.targetMarketValue
                            ) {
                              setShowCannotShortMessage(true)
                            } else {
                              setShowCannotShortMessage(false)
                            }
                          }}
                          max={isGlobal ? undefined : maxTargetMarketValue}
                          min={MinTargetMarketValue}
                          step={TargetMarketValueStep}
                          suffix={
                            ideaDetail && (
                              <IdeyaTypography.Text type={'secondary'}>
                                {`${t('idea_list.idea_modal.confirm_idea_form.current_mv')}: ${currencyValueFormatter(
                                  {
                                    value: ideaDetail.currentMarketValue,
                                  } as ValueFormatterParams,
                                  { decimal: 0, currency: 'USD' }
                                )}`}
                              </IdeyaTypography.Text>
                            )
                          }
                        />
                      </Form.Item>
                      {showRebalanceNote && (
                        <RebalanceNote
                          targetMarketValue={watchedTargetMarketValue}
                          currentMarketValue={currentMarketValue}
                          direction={ideaDetail?.direction as IdeaDirectionType}
                          isRebalanced={hasTargetMarketValueEdited}
                          onRebalance={handleRebalance}
                          onReset={handleRebalanceReset}
                        />
                      )}
                      {showCannotShortMessage && (
                        <span style={{ color: themeStyle.agGridTableRed }}>
                          {t(
                            'idea_list.idea_modal.action_idea_form.ticker_cannot_short_anymore'
                          )}
                        </span>
                      )}
                      <div
                        style={{
                          width: '100%',
                        }}
                      >
                        <Row
                          gutter={[
                            convertPxToNumber(themeStyle.marginXS),
                            convertPxToNumber(themeStyle.marginXS),
                          ]}
                          justify={isMobile ? 'space-between' : 'start'}
                          style={{
                            flexWrap: 'wrap',
                            width: '100%',
                          }}
                        >
                          {bpButtons.map(({ percentage, label }) => (
                            <Col key={label}>
                              <IdeyaButton
                                ideyaType={'antd'}
                                onClick={handleTargetMarketValueBp(percentage)}
                                style={{ width: isMobile ? '50px' : '' }}
                              >
                                {label}
                              </IdeyaButton>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </div>
                    <div style={{ width: '100%' }}>
                      <Form.Item
                        name={'target'}
                        style={{ width: '100%', marginBottom: '8px' }}
                        label={
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              width: '100%',
                              minWidth: 0,
                            }}
                          >
                            <IdeyaTypography.Text>
                              {t(
                                'idea_list.idea_modal.action_idea_form.target_price'
                              )}
                            </IdeyaTypography.Text>
                          </div>
                        }
                        rules={[
                          {
                            required: true,
                            message: t(
                              'idea_list.idea_modal.action_idea_form.target_price_required'
                            ),
                          },
                          {
                            validator: (_, value) => {
                              const direction = form.getFieldValue('direction')
                              if (
                                direction === IdeaDirectionType.Short &&
                                tickerPriceData &&
                                value > tickerPriceData.data.price
                              ) {
                                return Promise.reject(
                                  new Error(
                                    t(
                                      'idea_list.idea_modal.action_idea_form.price_above'
                                    )
                                  )
                                )
                              } else if (
                                direction === IdeaDirectionType.Long &&
                                tickerPriceData &&
                                value < tickerPriceData.data.price
                              ) {
                                return Promise.reject(
                                  new Error(
                                    t(
                                      'idea_list.idea_modal.action_idea_form.price_below'
                                    )
                                  )
                                )
                              }
                              return Promise.resolve()
                            },
                          },
                        ]}
                      >
                        <InputNumber<number>
                          min={0}
                          controls={false}
                          precision={2}
                          style={{ width: '100%' }}
                          formatter={(value) =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                          parser={(value) =>
                            value?.replace(
                              /\$\s?|(,*)/g,
                              ''
                            ) as unknown as number
                          }
                        />
                      </Form.Item>
                      <div style={{ width: '100%' }}>
                        <Row
                          gutter={[
                            convertPxToNumber(themeStyle.marginXS),
                            convertPxToNumber(themeStyle.marginXS),
                          ]}
                          justify={isMobile ? 'space-between' : 'start'}
                          style={{ flexWrap: 'wrap', width: '100%' }}
                        >
                          {targetPriceButtons.map(({ percentage, label }) => (
                            <Col key={label}>
                              <IdeyaButton
                                ideyaType={'antd'}
                                onClick={handleTargetPrice(percentage)}
                                style={{ width: isMobile ? '50px' : '' }}
                              >
                                {label}
                              </IdeyaButton>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </div>
                    <div style={{ width: '100%' }}>
                      <Form.Item
                        name={'stop'}
                        style={{ width: '100%', marginBottom: '8px' }}
                        label={
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              width: '100%',
                              minWidth: 0,
                            }}
                          >
                            <IdeyaTypography.Text>
                              {t(
                                'idea_list.idea_modal.action_idea_form.stop_price'
                              )}
                            </IdeyaTypography.Text>
                          </div>
                        }
                        rules={[
                          {
                            required: true,
                            message: t(
                              'idea_list.idea_modal.action_idea_form.stop_price_required'
                            ),
                          },
                          {
                            validator: (_, value) => {
                              const direction = form.getFieldValue('direction')
                              if (
                                direction === IdeaDirectionType.Short &&
                                tickerPriceData &&
                                value < tickerPriceData.data.price
                              ) {
                                return Promise.reject(
                                  new Error(
                                    t(
                                      'idea_list.idea_modal.action_idea_form.price_below'
                                    )
                                  )
                                )
                              } else if (
                                direction === IdeaDirectionType.Long &&
                                tickerPriceData &&
                                value > tickerPriceData.data.price
                              ) {
                                return Promise.reject(
                                  new Error(
                                    t(
                                      'idea_list.idea_modal.action_idea_form.price_above'
                                    )
                                  )
                                )
                              }
                              return Promise.resolve()
                            },
                          },
                        ]}
                      >
                        <InputNumber<number>
                          controls={false}
                          min={0}
                          precision={2}
                          style={{ width: '100%' }}
                          formatter={(value) =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                          parser={(value) =>
                            value?.replace(
                              /\$\s?|(,*)/g,
                              ''
                            ) as unknown as number
                          }
                        />
                      </Form.Item>
                      <div style={{ width: '100%' }}>
                        <Row
                          gutter={[
                            convertPxToNumber(themeStyle.marginXS),
                            convertPxToNumber(themeStyle.marginXS),
                          ]}
                          justify={isMobile ? 'space-between' : 'start'}
                          style={{ flexWrap: 'wrap', width: '100%' }}
                        >
                          {stopPriceButtons.map(({ percentage, label }) => (
                            <Col key={label}>
                              <IdeyaButton
                                ideyaType={'antd'}
                                onClick={handleStopPrice(percentage)}
                                style={{ width: isMobile ? '50px' : '' }}
                              >
                                {label}
                              </IdeyaButton>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </div>
                  </Row>
                </Col>
                <Col
                  sm={12}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px 0px' : '12px 0px 12px 20px',
                  }}
                >
                  <Row
                    style={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      alignSelf: 'stretch',
                    }}
                  >
                    <Form.Item
                      name={'fillStrategy'}
                      label={t(
                        'idea_list.idea_modal.action_idea_form.fill_strategy'
                      )}
                      style={{ width: '100%', marginBottom: '0px' }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'idea_list.idea_modal.action_idea_form.fill_strategy_required'
                          ),
                        },
                      ]}
                    >
                      <Radio.Group
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                        disabled={
                          fillStrategyLoading ||
                          (mode === 'edit' &&
                            fillStrategyData?.data.canFill !== true)
                        }
                        onChange={() => {
                          form.setFieldsValue({
                            auto_fill: true,
                          })
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: themeStyle.paddingXS,
                            borderRadius: themeStyle.borderRadiusBase,
                          }}
                        >
                          <Radio value={FillStrategies[0].value}>
                            <IdeyaTypography.Text>
                              {t(FillStrategies[0].labelKey)}
                            </IdeyaTypography.Text>
                          </Radio>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px',
                            borderRadius: themeStyle.borderRadiusBase,
                          }}
                        >
                          <Radio value={FillStrategies[1].value}>
                            {t(FillStrategies[1].labelKey)}
                          </Radio>
                        </div>
                      </Radio.Group>
                    </Form.Item>
                    <Form.Item shouldUpdate noStyle>
                      {({ getFieldValue }) => {
                        const fillStrategy = getFieldValue('fillStrategy')
                        return (
                          <Row
                            gutter={[
                              convertPxToNumber(themeStyle.marginSM),
                              isMobile
                                ? convertPxToNumber(themeStyle.marginSM)
                                : 0,
                            ]}
                            style={{
                              width: '100%',
                              backgroundColor: themeStyle.colorFillTertiary,
                              borderRadius: themeStyle.borderRadiusBase,
                              padding: themeStyle.marginSM,
                              // Cancel the gutter's negative margin so the card
                              // stays inside the modal body padding.
                              marginLeft: 0,
                              marginRight: 0,
                            }}
                          >
                            <Col md={{ flex: '0 0 40px' }}>
                              <Form.Item name={'auto_fill'} noStyle>
                                <Switch
                                  disabled={!fillStrategy}
                                  style={{ margin: 0 }}
                                />
                              </Form.Item>
                            </Col>
                            <Col md={{ flex: 1 }}>
                              <IdeyaTypography.Text
                                style={{
                                  color: fillStrategy
                                    ? undefined
                                    : themeStyle.colorTextDisabled,
                                }}
                              >
                                {t(
                                  'idea_list.idea_modal.action_idea_form.auto_fill_residual_order'
                                )}
                              </IdeyaTypography.Text>
                            </Col>
                            {fillStrategy && (
                              <Col span={24}>
                                <div
                                  style={{ marginLeft: isMobile ? 0 : '40px' }}
                                >
                                  <IdeyaTypography.Text
                                    style={{
                                      color: themeStyle.colorTextDisabled,
                                    }}
                                  >
                                    {t(
                                      'idea_list.idea_modal.action_idea_form.auto_fill_residual_description'
                                    )}
                                  </IdeyaTypography.Text>
                                </div>
                              </Col>
                            )}
                          </Row>
                        )
                      }}
                    </Form.Item>
                    {!isMobile && (
                      <Form.Item
                        name={'tradeReason'}
                        label={t(
                          'idea_list.idea_modal.action_idea_form.trade_reason'
                        )}
                        style={{ width: '100%', marginBottom: '0px' }}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'idea_list.idea_modal.action_idea_form.trade_reason_required'
                            ),
                          },
                        ]}
                      >
                        <TreeSelect
                          showSearch
                          style={{ width: '100%' }}
                          placeholder={t(
                            'idea_list.idea_modal.action_idea_form.select_trade_reason'
                          )}
                          allowClear
                          multiple
                          treeDefaultExpandAll
                          treeData={tradeReasonsTreeData}
                          treeCheckable={true}
                          listHeight={isMobile ? 300 : 500}
                          styles={{
                            popup: {
                              root: isMobile
                                ? {
                                    minWidth: 0,
                                    width: '90vw',
                                    maxWidth: '90vw',
                                  }
                                : {},
                            },
                          }}
                        />
                      </Form.Item>
                    )}
                    <Form.Item
                      name={'horizon'}
                      label={t('idea_list.idea_modal.action_idea_form.horizon')}
                      style={{ width: '100%', marginBottom: '0px' }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'idea_list.idea_modal.action_idea_form.horizon_required'
                          ),
                        },
                      ]}
                    >
                      <div style={{ width: '100%' }}>
                        <Row
                          gutter={[
                            isMobile
                              ? 0
                              : convertPxToNumber(themeStyle.marginXS),
                            convertPxToNumber(themeStyle.marginXS),
                          ]}
                          style={{
                            flexWrap: 'wrap',
                            width: '100%',
                          }}
                        >
                          {HorizonOptions.map((option) => (
                            <Col
                              key={option.value}
                              style={isMobile ? { flex: '1 0 160px' } : {}}
                            >
                              <IdeyaButton
                                ideyaType={
                                  horizonValue === option.value ? '' : 'antd'
                                }
                                onClick={() => handleHorizonClick(option.value)}
                                autoInsertSpace={false}
                                style={{
                                  justifyContent: 'center',
                                  display: 'flex',
                                  borderColor: themeStyle.colorSplit,
                                  width: isMobile ? '100%' : '',
                                }}
                              >
                                {t(option.labelKey)}
                              </IdeyaButton>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </Form.Item>
                    <Form.Item
                      name={'conviction'}
                      label={t(
                        'idea_list.idea_modal.action_idea_form.conviction'
                      )}
                      style={{ width: '100%', marginBottom: '0px' }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'idea_list.idea_modal.action_idea_form.conviction_required'
                          ),
                        },
                      ]}
                    >
                      <div style={{ width: '100%' }}>
                        <Row
                          gutter={[
                            isMobile
                              ? 0
                              : convertPxToNumber(themeStyle.marginXS),
                            convertPxToNumber(themeStyle.marginXS),
                          ]}
                          style={{ flexWrap: 'wrap', width: '100%' }}
                        >
                          {ConvictionOptions.map((option) => (
                            <Col
                              key={option.value}
                              style={isMobile ? { flex: '1 0 160px' } : {}}
                            >
                              <IdeyaButton
                                ideyaType={
                                  convictionValue === option.value ? '' : 'antd'
                                }
                                onClick={() =>
                                  handleConvictionClick(option.value)
                                }
                                autoInsertSpace={false}
                                style={{
                                  justifyContent: 'center',
                                  display: 'flex',
                                  borderColor: themeStyle.colorSplit,
                                  width: isMobile ? '100%' : '',
                                }}
                              >
                                {t(option.labelKey)}
                              </IdeyaButton>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </Form.Item>
                    {isMobile && (
                      <>
                        <Form.Item
                          name={'tradeReason'}
                          label={t(
                            'idea_list.idea_modal.action_idea_form.trade_reason'
                          )}
                          style={{ width: '100%', marginBottom: '0px' }}
                          rules={[
                            {
                              required: true,
                              message: t(
                                'idea_list.idea_modal.action_idea_form.trade_reason_required'
                              ),
                            },
                          ]}
                        >
                          <Checkbox.Group
                            style={{ width: '100%' }}
                            onChange={(checkedValues) =>
                              setSelectedTradeReasons(checkedValues as string[])
                            }
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                width: '100%',
                              }}
                            >
                              {tradeReasonsTreeData.map((category) => (
                                <div key={category.value}>
                                  {category.children ? (
                                    <>
                                      <IdeyaTypography.Text type={'secondary'}>
                                        {category.title}
                                      </IdeyaTypography.Text>
                                      {category.children.map((child) => (
                                        <div
                                          key={child.value}
                                          style={{
                                            borderWidth: '1px',
                                            borderStyle: 'solid',
                                            borderColor:
                                              selectedTradeReasons.includes(
                                                child.value
                                              )
                                                ? themeStyle.colorSplit
                                                : themeStyle.colorBorder,
                                            borderRadius: `${themeStyle.borderRadiusBase}`,
                                            marginBottom: '8px',
                                            backgroundColor:
                                              selectedTradeReasons.includes(
                                                child.value
                                              )
                                                ? themeStyle.blue6
                                                : '',
                                          }}
                                        >
                                          <Checkbox
                                            value={child.value}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              width: '100%',
                                              padding: `6px 15px`,
                                              color:
                                                selectedTradeReasons.includes(
                                                  child.value
                                                )
                                                  ? themeStyle.blue9
                                                  : themeStyle.colorText,
                                            }}
                                          >
                                            {child.title}
                                          </Checkbox>
                                        </div>
                                      ))}
                                    </>
                                  ) : (
                                    <div
                                      style={{
                                        borderWidth: '1px',
                                        borderStyle: 'solid',
                                        borderColor:
                                          selectedTradeReasons.includes(
                                            category.value
                                          )
                                            ? themeStyle.colorSplit
                                            : themeStyle.colorBorder,
                                        borderRadius: `${themeStyle.borderRadiusBase}`,
                                        backgroundColor:
                                          selectedTradeReasons.includes(
                                            category.value
                                          )
                                            ? themeStyle.blue6
                                            : '',
                                      }}
                                    >
                                      <Checkbox
                                        value={category.value}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          width: '100%',
                                          padding: `6px 15px`,
                                          color: selectedTradeReasons.includes(
                                            category.value
                                          )
                                            ? themeStyle.blue9
                                            : themeStyle.colorText,
                                        }}
                                      >
                                        {category.title}
                                      </Checkbox>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </Checkbox.Group>
                        </Form.Item>
                      </>
                    )}
                    {/*
                // Hiding for now until compliance gives ok
                <Form.Item
                  label={t('idea_list.idea_modal.action_idea_form.comment')}
                  name={'comment'}
                  style={{ width: '100%' }}
                >
                  <Input.TextArea
                    rows={isMobile ? 3 : 1}
                    style={{ width: '100%' }}
                    placeholder={t(
                      'idea_list.idea_modal.action_idea_form.add_comment'
                    )}
                  />
                </Form.Item>*/}
                  </Row>
                </Col>
              </Row>
            </div>
          </div>
          <div className={'action-idea-form__footer'}>
            <Row
              justify={'space-between'}
              className={'action-idea-form__footer-row'}
              style={{
                flexDirection: 'row',
                gap: isMobile ? 8 : 0,
              }}
            >
              <Col style={isMobile ? { marginBottom: 8 } : {}}>
                <IdeyaButton
                  type={'text'}
                  onClick={() => {
                    form.resetFields()
                    setHorizonValue('')
                    setConvictionValue('')
                    setCompanyName('')
                    setIsMaxFillReached(false)
                    setVisualDirection('')
                  }}
                  disabled={mode === 'edit'}
                  style={{
                    color: themeStyle.colorPrimaryText,
                  }}
                >
                  {t('idea_list.idea_modal.action_idea_form.clear_form')}
                </IdeyaButton>
              </Col>
              <Col>
                <Row gutter={[8, 8]}>
                  {mode === 'create' ? (
                    <>
                      <Col>
                        <IdeyaButton ghost onClick={onCloseForm}>
                          {t('common.cancel')}
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton onClick={onConfirmForm}>
                          {t(
                            'idea_list.idea_modal.action_idea_form.review_idea'
                          )}
                        </IdeyaButton>
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col>
                        <IdeyaButton
                          type={'primary'}
                          danger
                          onClick={onIdeaLiquidated}
                        >
                          {t('idea_list.idea_modal.action_idea_form.liquidate')}
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          onClick={onConfirmForm}
                          disabled={!hasEditedUpdateValues}
                        >
                          {t(
                            'idea_list.idea_modal.action_idea_form.review_idea'
                          )}
                        </IdeyaButton>
                      </Col>
                    </>
                  )}
                </Row>
              </Col>
            </Row>
          </div>
        </Form>
      </>
    )
  }
)
